// Pure derivations turning the analytics `series[]` into the per-day presence
// tracks the Sessions tab draws. No React, no ambient clock — `now` is always
// injected so every branch is testable, following `insightDerive.js`.
//
// TIMEZONE: clock positions are read in whatever zone the caller passes, which
// in the app is the viewer's own. `timeZone` defaults to "UTC" everywhere, so
// omitting it reproduces the original behaviour exactly.
//
// What does NOT move to the viewer's zone: `series[].date` is a UTC day bucket
// and `online_seconds` is overlap-clamped to it, so the row's identity and its
// total stay UTC. Re-attributing those to local calendar days would mean
// abandoning the rollup for a sum of `sessions[]` that under-counts (see the
// note in `buildSessionDays`) and can be truncated outright. The minority of
// blocks whose local date differs from their row's is labelled, not hidden.

/** Track window, matching the design's 06:00 → 23:00 axis. */
export const TRACK_START_HOUR = 6;
export const TRACK_END_HOUR = 23;

/** The design's window, used whenever every block fits inside it. */
export const DEFAULT_TRACK_WINDOW = { startHour: TRACK_START_HOUR, endHour: TRACK_END_HOUR };

/** Fallback window for zones that push a coach's hours outside the design window. */
export const FULL_DAY_WINDOW = { startHour: 0, endHour: 24 };

/** How a session ended. Drives the block colour and the legend. */
export const SESSION_END = {
  /** Still connected right now. */
  OPEN: "open",
  /** The app never reported a sign-out; the stale sweep guessed the end. */
  ESTIMATED: "estimated",
  /** A real disconnect was recorded. */
  CLEAN: "clean",
};

/** ms-agents writes this reason when the sweep closed a session it never saw end. */
const STALE_SWEEP_REASON = "stale_sweep";

const MS_PER_DAY = 86400000;

/**
 * Classify one session by how it ended.
 *
 * @param {Object} session - A `series[].sessions[]` record.
 * @returns {'open'|'estimated'|'clean'}
 */
export function sessionEndKind(session) {
  if (!session) return SESSION_END.CLEAN;
  if (session.is_active === true || session.disconnected_at == null) {
    return SESSION_END.OPEN;
  }
  return session.disconnect_reason === STALE_SWEEP_REASON
    ? SESSION_END.ESTIMATED
    : SESSION_END.CLEAN;
}

// One `Intl.DateTimeFormat` per zone, reused. A 90-day range runs this hundreds
// of times per render and constructing the formatter is the expensive part.
const FORMATTER_CACHE = new Map();

function zoneFormatter(timeZone) {
  const cached = FORMATTER_CACHE.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  FORMATTER_CACHE.set(timeZone, formatter);
  return formatter;
}

/**
 * Wall-clock parts of an instant, as read in `timeZone`.
 *
 * "UTC" short-circuits to `getUTC*` and never touches `Intl`, which keeps the
 * default path identical to the original implementation. An unusable zone falls
 * back to UTC rather than blanking the panel — callers validate first via
 * `isValidTimeZone`, so reaching that branch means the runtime lacks the zone.
 *
 * @param {string|null|undefined} iso
 * @param {string} [timeZone='UTC'] - IANA zone id.
 * @returns {{year:number,month:number,day:number,hour:number,minute:number,second:number}|null}
 *   Null when absent or unparseable.
 */
export function zonedParts(iso, timeZone = "UTC") {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const date = new Date(ms);

  if (timeZone === "UTC") {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      hour: date.getUTCHours(),
      minute: date.getUTCMinutes(),
      second: date.getUTCSeconds(),
    };
  }

  let parts;
  try {
    parts = zoneFormatter(timeZone).formatToParts(date);
  } catch {
    return zonedParts(iso, "UTC");
  }

  const read = {};
  parts.forEach((part) => {
    if (part.type !== "literal") read[part.type] = Number(part.value);
  });

  return {
    year: read.year,
    month: read.month,
    day: read.day,
    // h23 should already give 0–23, but midnight has historically read as 24
    // in some implementations and that would push a block off the track.
    hour: read.hour === 24 ? 0 : read.hour,
    minute: read.minute,
    second: read.second,
  };
}

/**
 * Fractional hour-of-day of an ISO timestamp, read in `timeZone` (13:30 → 13.5).
 *
 * @param {string|null|undefined} iso
 * @param {string} [timeZone='UTC']
 * @returns {number|null} 0–24, or null when absent or unparseable.
 */
export function zonedHour(iso, timeZone = "UTC") {
  const parts = zonedParts(iso, timeZone);
  if (!parts) return null;
  return parts.hour + parts.minute / 60 + parts.second / 3600;
}

/**
 * Calendar date of an instant in `timeZone`, as "YYYY-MM-DD".
 *
 * @param {string|null|undefined} iso
 * @param {string} [timeZone='UTC']
 * @returns {string|null}
 */
export function zonedDateKey(iso, timeZone = "UTC") {
  const parts = zonedParts(iso, timeZone);
  if (!parts) return null;
  const { year, month, day } = parts;
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(
    day
  ).padStart(2, "0")}`;
}

/**
 * Fractional UTC hour of an ISO timestamp (13:30 → 13.5).
 *
 * @param {string|null|undefined} iso
 * @returns {number|null} Null when absent or unparseable.
 */
export function utcHour(iso) {
  return zonedHour(iso, "UTC");
}

/** Whole days from `fromKey` to `toKey`, clamped to -1/0/+1. Both "YYYY-MM-DD". */
function dayShift(fromKey, toKey) {
  if (!fromKey || !toKey) return 0;
  const from = Date.parse(`${fromKey}T00:00:00Z`);
  const to = Date.parse(`${toKey}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  const days = Math.round((to - from) / MS_PER_DAY);
  return Math.max(-1, Math.min(1, days));
}

/**
 * Which window the axis should use.
 *
 * Two states rather than a fitted min/max: a continuously-adaptive axis rescales
 * on every range change, so two screenshots of the same coach stop being
 * comparable. Falls back to the full day only when a block would otherwise be
 * clamped onto a track edge — which is what a westward zone shift causes.
 *
 * @param {Array<Object>} days - Output of `buildSessionDays`.
 * @returns {{startHour:number,endHour:number}}
 */
export function trackWindow(days) {
  const list = Array.isArray(days) ? days : [];
  const fits = list.every((day) =>
    (Array.isArray(day?.sessions) ? day.sessions : []).every(
      (s) => s.startHour >= TRACK_START_HOUR && s.endHour <= TRACK_END_HOUR
    )
  );
  return fits ? DEFAULT_TRACK_WINDOW : FULL_DAY_WINDOW;
}

/**
 * Position of an hour on the track, as a 0–100 percentage.
 * Hours outside the window clamp to the ends rather than overflowing the track.
 *
 * @param {number|null} hour
 * @param {{startHour:number,endHour:number}} [window=DEFAULT_TRACK_WINDOW]
 * @returns {number|null}
 */
export function trackPercent(hour, window = DEFAULT_TRACK_WINDOW) {
  if (hour == null || !Number.isFinite(hour)) return null;
  const span = window.endHour - window.startHour;
  if (!(span > 0)) return null;
  const raw = ((hour - window.startHour) / span) * 100;
  return Math.max(0, Math.min(100, raw));
}

/** Median of a numeric list, or null when empty. Even lengths take the mean of the middle pair. */
export function median(values) {
  const nums = values.filter((v) => v != null && Number.isFinite(v)).sort((a, b) => a - b);
  if (nums.length === 0) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 === 1 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
}

/**
 * One session laid out on the track, in `timeZone`.
 * A still-open session is drawn to "now" when the bucket is today, and to its
 * `last_seen_at` otherwise — never to the end of the axis, which would claim
 * hours the coach may not have worked.
 */
function layoutSession(session, isToday, now, timeZone, rowDate) {
  const startIso = session?.connected_at ?? null;
  const startHour = zonedHour(startIso, timeZone);
  if (startHour == null) return null;

  const kind = sessionEndKind(session);
  const endIso =
    (kind === SESSION_END.OPEN
      ? isToday
        ? now.toISOString()
        : session?.last_seen_at
      : (session?.disconnected_at ?? session?.last_seen_at)) ?? null;

  // An open session with no end signal at all still deserves a visible mark, so
  // it gets a hairline rather than being dropped.
  const rawEndHour = zonedHour(endIso, timeZone) ?? startHour;

  const startMs = Date.parse(startIso);
  const endMs = endIso ? Date.parse(endIso) : NaN;
  const elapsedMs = Number.isNaN(startMs) || Number.isNaN(endMs) ? null : endMs - startMs;

  // A session running past local midnight comes back with an end hour earlier
  // than its start. Left to the Math.max below it would collapse to a zero-width
  // block, so it runs to the end of the day instead and says that it wrapped.
  const wrapsMidnight = elapsedMs != null && elapsedMs > 0 && rawEndHour < startHour;
  const endHour = wrapsMidnight ? 24 : Math.max(startHour, rawEndHour);

  const declared = Number(session?.duration_seconds);
  const durationSeconds =
    session?.duration_seconds != null && Number.isFinite(declared)
      ? declared
      : // The instants outrank the drawn hours: the wrap clamp above moves
        // endHour, and must never be allowed to change the reported length.
        elapsedMs != null && elapsedMs > 0
        ? Math.round(elapsedMs / 1000)
        : Math.round((endHour - startHour) * 3600);

  return {
    startHour,
    endHour,
    kind,
    startIso,
    endIso,
    wrapsMidnight,
    startDayShift: dayShift(rowDate, zonedDateKey(startIso, timeZone)),
    endDayShift: dayShift(rowDate, zonedDateKey(endIso, timeZone)),
    platform: session?.platform || null,
    durationSeconds,
  };
}

/**
 * Group the analytics series into one presence track per day, newest first.
 *
 * Only day-granularity series make sense here: a weekly bucket's `sessions[]`
 * spans seven different clocks, so laying them on one 24h track would be a lie.
 * Weekly input returns an empty list and the panel says why.
 *
 * @param {Array<Object>|null|undefined} series - `series[]` from the coach analytics payload.
 * @param {Object} [options]
 * @param {'day'|'week'} [options.granularity='day']
 * @param {Date} [options.now]
 * @param {string} [options.timeZone='UTC'] - Zone the clock positions are read in.
 * @returns {Array<Object>} `{ date, isToday, sessions, totalSeconds, count,
 *   estimatedCount, hasOpen, hasDayShift, platforms, truncated }`, newest first.
 */
export function buildSessionDays(
  series,
  { granularity = "day", now = new Date(), timeZone = "UTC" } = {}
) {
  if (granularity !== "day") return [];
  const rows = Array.isArray(series) ? series : [];
  // Deliberately UTC: `row.date` is a UTC bucket key, so this is a same-space
  // comparison. Localising one side would break bucket identity and drop an
  // open session on the current bucket through to `last_seen_at` below.
  const todayKey = now.toISOString().slice(0, 10);

  return rows
    .map((row) => {
      const date = String(row?.date ?? "").slice(0, 10);
      const isToday = date === todayKey;
      const sessions = (Array.isArray(row?.sessions) ? row.sessions : [])
        .map((s) => layoutSession(s, isToday, now, timeZone, date))
        .filter(Boolean);

      const platforms = [];
      sessions.forEach((s) => {
        if (s.platform && !platforms.includes(s.platform)) platforms.push(s.platform);
      });

      return {
        date,
        isToday,
        sessions,
        // Prefer the rollup's own total: it is overlap-clamped to the bucket,
        // so it stays right on a day whose session started the day before —
        // exactly the case where summing the listed sessions would under-count.
        totalSeconds:
          row?.online_seconds != null && Number.isFinite(Number(row.online_seconds))
            ? Number(row.online_seconds)
            : sessions.reduce((sum, s) => sum + s.durationSeconds, 0),
        count: sessions.length,
        estimatedCount: sessions.filter((s) => s.kind === SESSION_END.ESTIMATED).length,
        hasOpen: sessions.some((s) => s.kind === SESSION_END.OPEN),
        hasDayShift: sessions.some(
          (s) => s.startDayShift !== 0 || s.endDayShift !== 0 || s.wrapsMidnight
        ),
        platforms,
        truncated: row?.sessions_truncated === true,
      };
    })
    .filter((day) => day.date)
    .reverse();
}

/**
 * The four headline numbers above the tracks.
 *
 * "Usual day" is the median over days the coach actually signed in — averaging
 * in their days off would report a working day nobody works.
 *
 * Start and finish hours inherit whatever zone `buildSessionDays` used; there is
 * no zone argument here because there is nothing left to convert.
 *
 * @param {Array<Object>} days - Output of `buildSessionDays`.
 * @returns {{ medianOnlineSeconds: number|null, daysSignedIn: number,
 *   totalDays: number, medianStartHour: number|null, medianEndHour: number|null,
 *   estimatedEnds: number }}
 */
export function summariseSessionDays(days) {
  const list = Array.isArray(days) ? days : [];
  const worked = list.filter((d) => d.totalSeconds > 0 || d.count > 0);

  return {
    medianOnlineSeconds: median(worked.map((d) => d.totalSeconds)),
    daysSignedIn: worked.length,
    totalDays: list.length,
    medianStartHour: median(
      worked.map((d) => (d.sessions.length ? d.sessions[0].startHour : null))
    ),
    medianEndHour: median(
      worked.map((d) => (d.sessions.length ? d.sessions[d.sessions.length - 1].endHour : null))
    ),
    estimatedEnds: list.reduce((sum, d) => sum + d.estimatedCount, 0),
  };
}

/**
 * Fractional hour as a clock label ("8.1" → "08:06").
 * A block running to the end of the day reads "24:00", not "00:00" — the start
 * of the day printed at the end of a block would read as a bug.
 *
 * @param {number|null} hour
 * @returns {string}
 */
export function formatClockHour(hour) {
  if (hour == null || !Number.isFinite(hour)) return "—";
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  // 59.7 minutes must roll the hour, not print ":60".
  const carried = m === 60 ? h + 1 : h;
  const minutes = m === 60 ? 0 : m;
  if (carried >= 24) return "24:00";
  return `${String(carried).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}
